// View type mapping for URL parameters
import { CustomTrail, CustomTrailStop, TrailMatch } from "../types/tour";

export const ViewType = {
  trails: "t",
  destinations: "d",
  directions: "r",
  standalone: "s",
} as const;

// Reverse mapping for parsing
export const ViewTypeReverse = {
  t: "trails",
  d: "destinations",
  r: "directions",
  s: "standalone",
} as const;

interface ParsedLocation {
  trailId: string;
  pointIndex: number;
  fromView?: string; // Optional: 'trails', 'destinations', 'directions', 'standalone'
  // For directions format: startStopId:endStopId:r
  startStopId?: string;
  endStopId?: string;
  isDirections?: boolean;
  // For standalone format: stopId:0:s
  stopId?: number;
  isStandalone?: boolean;
}

/**
 * Extract the loc parameter from current URL
 * Handles both standard query params and hash-based routing
 */
export const getLocParameterFromURL = (url?: string): string | null => {
  try {
    const currentUrl = url || window.location.href;
    const urlObj = new URL(currentUrl);

    // Check standard query parameters first
    if (urlObj.search) {
      const urlParams = new URLSearchParams(urlObj.search);
      const locParam = urlParams.get("loc");
      if (locParam) return locParam;
    }

    // Check hash-based parameters (for React Router)
    if (urlObj.hash && urlObj.hash.includes("?")) {
      const hashSearchString = urlObj.hash.split("?")[1];
      const hashParams = new URLSearchParams(hashSearchString);
      return hashParams.get("loc");
    }

    return null;
  } catch (error) {
    // Fallback for invalid URLs
    return null;
  }
};

/**
 * Parse the loc query parameter into its components
 * Format: <trail_id>:<point_index> or <trail_id>:<point_index>:<view_type>
 * Or for directions: <start_stop_id>:<end_stop_id>:r
 * Validates input to prevent malicious data and ensure data integrity
 */
export const parseTourLocation = (
  loc: string | null | undefined
): ParsedLocation | null => {
  if (!loc) return null;

  // Validate total input length to prevent memory exhaustion
  if (loc.length > 500) {
    return null;
  }

  // Split and validate format
  const params = loc.split(":");
  if (params.length < 2 || params.length > 3) {
    return null;
  }

  // Check if this is a directions format (ends with :r)
  if (params.length === 3 && params[2] === "r") {
    // Directions format: startStopId:endStopId:r
    const startStopId = params[0];
    const endStopId = params[1];

    // Validate stop IDs
    if (
      !startStopId ||
      !/^[a-zA-Z0-9]+$/.test(startStopId) ||
      startStopId.length > 50
    ) {
      return null;
    }
    if (
      !endStopId ||
      !/^[a-zA-Z0-9]+$/.test(endStopId) ||
      endStopId.length > 50
    ) {
      return null;
    }

    return {
      trailId: "",
      pointIndex: 0,
      fromView: "directions",
      startStopId,
      endStopId,
      isDirections: true,
    };
  }

  // Check if this is a standalone stop format (ends with :s)
  if (params.length === 3 && params[2] === "s") {
    // Standalone format: stopId:0:s
    const stopIdParam = params[0];

    // Validate stop ID (must be numeric for standalone stops)
    const stopId = parseInt(stopIdParam, 10);
    if (
      Number.isNaN(stopId) ||
      stopId < 0 ||
      stopId > Number.MAX_SAFE_INTEGER
    ) {
      return null;
    }

    return {
      trailId: "",
      pointIndex: 0,
      fromView: "standalone",
      stopId,
      isStandalone: true,
    };
  }

  // Standard trail format: trailId:pointIndex or trailId:pointIndex:view
  const trailId = params[0];
  if (!trailId || !/^[a-zA-Z0-9]+$/.test(trailId) || trailId.length > 50) {
    return null;
  }

  // Validate point index
  const pointIndexParsed = parseInt(params[1], 10);
  if (
    Number.isNaN(pointIndexParsed) ||
    pointIndexParsed < 0 ||
    pointIndexParsed > Number.MAX_SAFE_INTEGER
  ) {
    return null;
  }

  const result: ParsedLocation = {
    trailId,
    pointIndex: pointIndexParsed,
    isDirections: false,
  };

  // Validate optional view parameter
  if (params.length >= 3 && params[2]) {
    const viewParam = params[2];

    // Validate view parameter format
    if (!/^[a-z]$/.test(viewParam)) {
      return null;
    }

    // Safe property access with validation
    const validViewTypes = Object.keys(ViewTypeReverse);
    if (validViewTypes.includes(viewParam)) {
      const viewType =
        ViewTypeReverse[viewParam as keyof typeof ViewTypeReverse];
      result.fromView = viewType;
    }
    // Ignore invalid view parameter, don't fail entire parse
  }

  return result;
};

/**
 * Parse the current URL's loc parameter into components
 * Convenience function that combines URL extraction and parsing
 */
export const parseCurrentURLLocation = (): ParsedLocation | null => {
  const locParam = getLocParameterFromURL();
  return parseTourLocation(locParam);
};

/**
 * Find a trail by its ID in the trails array
 */
export const findTrailById = (
  trails: CustomTrail[],
  trailId: string
): TrailMatch | null => {
  if (!trails || !Array.isArray(trails) || trails.length === 0) {
    return null;
  }

  const index = trails.findIndex(
    (trail) =>
      trail?.trailid === trailId ||
      trail?.id === trailId ||
      trail?.id === parseInt(trailId, 10)
  );

  if (index === -1) {
    return null;
  }

  return {
    trail: trails[index],
    trailIndex: index,
  };
};

/**
 * Validate and clamp a point index to valid range
 */
export const validatePointIndex = (
  points: CustomTrailStop[] | undefined,
  index: number
): number => {
  if (!points || !Array.isArray(points) || points.length === 0) {
    return 0;
  }

  if (Number.isNaN(index) || index < 0) {
    return 0;
  }

  return Math.min(index, points.length - 1);
};

/**
 * Generate a loc parameter string from components
 */
export const generateLocParameter = (
  trailId: string,
  pointIndex: number,
  currentView?: string
): string => {
  let result = `${trailId}:${pointIndex}`;

  // Add view parameter if specified
  if (currentView && currentView in ViewType) {
    const viewCode = ViewType[currentView as keyof typeof ViewType];
    result += `:${viewCode}`;
  }

  return result;
};

/**
 * Generate a loc parameter string for directions
 */
export const generateDirectionsLocParameter = (
  startStopId: string,
  endStopId: string
): string => {
  return `${startStopId}:${endStopId}:r`;
};

/**
 * Generate a loc parameter string for standalone stops
 */
export const generateStandaloneLocParameter = (stopId: number): string => {
  return `${stopId}:0:s`;
};

/**
 * Update the browser URL with the current location
 */
export const updateURLWithLocation = (
  trailId: string,
  pointIndex: number,
  currentView?: string
): void => {
  try {
    // Validate inputs first to prevent injection attacks
    if (!trailId || !/^[a-zA-Z0-9]+$/.test(trailId)) {
      throw new Error("Invalid trail ID");
    }
    if (pointIndex < 0 || !Number.isInteger(pointIndex)) {
      throw new Error("Invalid point index");
    }
    if (
      currentView &&
      !["trails", "destinations", "directions"].includes(currentView)
    ) {
      throw new Error("Invalid view type");
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("loc");

    // Safe manual construction with validated inputs for clean URLs
    const locParam = generateLocParameter(trailId, pointIndex, currentView);
    const baseUrl = url.toString();
    const separator = baseUrl.includes("?") ? "&" : "?";
    const newUrl = `${baseUrl}${separator}loc=${locParam}`;

    // Validate final URL before using it
    // eslint-disable-next-line no-new
    new URL(newUrl); // Will throw if invalid

    // Use replaceState to avoid creating browser history entries for each navigation
    window.history.replaceState({}, "", newUrl);
  } catch (error) {
    // Fallback to fully safe method if anything goes wrong
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(
        "loc",
        generateLocParameter(trailId, pointIndex, currentView)
      );
      window.history.replaceState({}, "", url.toString());
    } catch (fallbackError) {
      // Silently fail if both methods fail
    }
  }
};

/**
 * Update the browser URL with directions
 */
export const updateURLWithDirections = (
  startStopId: string,
  endStopId: string
): void => {
  try {
    // Validate inputs first to prevent injection attacks
    if (!startStopId || !/^[a-zA-Z0-9]+$/.test(startStopId)) {
      throw new Error("Invalid start stop ID");
    }
    if (!endStopId || !/^[a-zA-Z0-9]+$/.test(endStopId)) {
      throw new Error("Invalid end stop ID");
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("loc");

    // Safe manual construction with validated inputs for clean URLs
    const locParam = generateDirectionsLocParameter(startStopId, endStopId);
    const baseUrl = url.toString();
    const separator = baseUrl.includes("?") ? "&" : "?";
    const newUrl = `${baseUrl}${separator}loc=${locParam}`;

    // Validate final URL before using it
    // eslint-disable-next-line no-new
    new URL(newUrl); // Will throw if invalid

    // Use replaceState to avoid creating browser history entries for each navigation
    window.history.replaceState({}, "", newUrl);
  } catch (error) {
    // Fallback to fully safe method if anything goes wrong
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(
        "loc",
        generateDirectionsLocParameter(startStopId, endStopId)
      );
      window.history.replaceState({}, "", url.toString());
    } catch (fallbackError) {
      // Silently fail if both methods fail
    }
  }
};

/**
 * Update the browser URL with a standalone stop
 */
export const updateURLWithStandaloneStop = (stopId: number): void => {
  try {
    // Validate input first to prevent injection attacks
    if (stopId < 0 || !Number.isInteger(stopId)) {
      throw new Error("Invalid stop ID");
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("loc");

    // Safe manual construction with validated input for clean URLs
    const locParam = generateStandaloneLocParameter(stopId);
    const baseUrl = url.toString();
    const separator = baseUrl.includes("?") ? "&" : "?";
    const newUrl = `${baseUrl}${separator}loc=${locParam}`;

    // Validate final URL before using it
    // eslint-disable-next-line no-new
    new URL(newUrl); // Will throw if invalid

    // Use replaceState to avoid creating browser history entries for each navigation
    window.history.replaceState({}, "", newUrl);
  } catch (error) {
    // Fallback to fully safe method if anything goes wrong
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("loc", generateStandaloneLocParameter(stopId));
      window.history.replaceState({}, "", url.toString());
    } catch (fallbackError) {
      // Silently fail if both methods fail
    }
  }
};

/**
 * Remove the loc parameter from the URL
 */
export const clearLocationFromURL = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("loc");
    window.history.replaceState({}, "", url.toString());
  } catch (error) {
    // Silently fail if URL clearing fails
  }
};
